import { type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import { Money } from '../../components/Money';
import type { Receipt } from '@deposits/shared';

interface SummaryBarProps {
  receipts: Receipt[];
  className?: string;
}

interface SummaryTotals {
  checkCount: number;
  checksTotal: number;
  cashTotal: number;
  grandTotal: number;
}

export function summarize(receipts: Receipt[]): SummaryTotals {
  let checkCount = 0;
  let checksTotal = 0;
  let cashTotal = 0;
  for (const r of receipts) {
    if (r.payment_type === 'check') {
      checkCount += 1;
      checksTotal += r.amount;
    } else if (r.payment_type === 'cash') {
      cashTotal += r.amount;
    }
  }
  return {
    checkCount,
    checksTotal,
    cashTotal,
    grandTotal: checksTotal + cashTotal,
  };
}

/**
 * Inline centered summary — matches the Arbox Expenses pattern:
 * `LABEL <bold value>` segments separated by tiny circle bullets, all
 * centered. 0.9rem text, color #3b3b3b, gap 24px.
 *
 * Color is set on EACH span explicitly because parent inheritance is
 * unreliable here (bidi isolation + antd Money child wrap can break the
 * cascade — same reason the Expenses HTML repeats `color: rgb(59,59,59)`
 * on every span).
 *
 * Sums whatever is currently in the filtered table (selection-independent).
 */
export function SummaryBar({ receipts, className }: SummaryBarProps) {
  const t = summarize(receipts);
  return (
    <div
      className={`bg-blue-bg border-t border-border px-2 py-3 flex items-center justify-center flex-wrap ${className ?? ''}`}
      style={{ gap: 24 }}
    >
      <Stat label="כמות צ'קים להפקדה">
        <span dir="ltr">{t.checkCount}</span>
      </Stat>
      <Bullet />
      <Stat label="שווי להפקדה (צ'קים)">
        <Money amount={t.checksTotal} />
      </Stat>
      <Bullet />
      <Stat label='סה"כ מזומן'>
        <Money amount={t.cashTotal} />
      </Stat>
      <Bullet />
      <Stat label='סה"כ להפקדה'>
        <Money amount={t.grandTotal} />
      </Stat>
    </div>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span style={{ fontSize: '0.9rem', color: 'rgb(59, 59, 59)' }}>
      {label}{' '}
      <span style={{ fontFamily: 'Ploni, sans-serif', fontWeight: 600 }}>{children}</span>
    </span>
  );
}

function Bullet() {
  return (
    <FontAwesomeIcon
      icon={faCircle}
      aria-hidden
      style={{ fontSize: 3, color: 'rgb(59, 59, 59)' }}
    />
  );
}
